import React, { useCallback } from 'react';
import DOMPurify from 'dompurify';
import { QuestionApiType } from 'pages/new-questions/types';
import { ListStyled, ItemStyled, NoQuestionFindArea, CleanButton } from './styles';
import Loading from 'components/Loading';
import finder from 'img/finder.svg';
import { Pagination } from '../Pagination';

type Props = {
  loading: boolean;
  questions: QuestionApiType[];
  currentPage: number;
  /** O 2º argumento é a etapa da linha, quando ela representa uma etapa de caso. */
  handleQuestionClick: (questionId: string, questionStepId?: number | null) => void;
  handleClearForm: () => void;
  totalPages: number;
  onPageChange: (page: number) => void;
};

const escapeHtml = (text: string) => {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
};

export const QuestionList: React.FC<Props> = ({
  loading,
  questions,
  currentPage,
  handleQuestionClick,
  handleClearForm,
  totalPages,
  onPageChange,
}) => {
  const totalQuestions = questions?.length;

  const handleClick = useCallback(
    (questionId: string, questionStepId?: number | null) => {
      return () => handleQuestionClick(questionId, questionStepId);
    },
    [handleQuestionClick]
  );

  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent, questionId: string, questionStepId?: number | null) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleQuestionClick(questionId, questionStepId);
      }
    },
    [handleQuestionClick]
  );

  if (loading) {
    return <Loading height="calc(100vh - 360px)" />;
  }

  if (totalQuestions > 0)
    return (
      <>
        <ListStyled>
          {questions?.map((question) => (
            <ItemStyled
              // A listagem devolve uma linha por etapa: um Estudo de Caso repete o
              // mesmo id_question e só a etapa distingue as linhas irmãs.
              key={`${question.id_question}-${question.question_step_id ?? 0}`}
              onClick={handleClick(String(question.id_question), question.question_step_id)}
              role="button"
              tabIndex={0}
              onKeyPress={(e) =>
                handleKeyPress(e, String(question.id_question), question.question_step_id)
              }
              aria-label={`Questão ${question.code}`}
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(
                  `(${escapeHtml(question.code ?? 'N/A')}) ${
                    question.statement || question.summary || 'Sem descrição'
                  }`
                ),
              }}
            />
          ))}
        </ListStyled>
        <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={onPageChange} />
      </>
    );
  else
    return (
      <NoQuestionFindArea>
        <img src={finder} alt="" />
        <p>
          Não conseguimos encontrar nenhuma questão. <br />
          Talvez você tenha escrito algo errado?
        </p>
        <CleanButton onClick={handleClearForm}>Limpar pesquisa</CleanButton>
      </NoQuestionFindArea>
    );
};
